import React, { useState, useEffect, useRef } from 'react';
import {
  DateTimeFormatter,
  EventEmitter,
  getCurrentUserHook,
  getTranslatedText,
  useIntl,
  useUserMSPCheckHook
} from '@nile/components';
import shortid from 'shortid';
import { Tooltip } from 'react-clone-tippy';
import { SVGIcon } from '@superops/super-icons';
import {
  AccountClient,
  GET_CALENDAR_INTEGRATED,
  GET_WIDGET_APPOINTMENT_COUNT,
  TicketClient,
  useCachedQuery,
  useQuery
} from '@nile/graphql/src';
import { getDayDetails } from '@nile/mangifera/src/Pages/SchedulingPage/helpers';
import { Button, Card, TextStyle } from '@superops/ui-design-system';
import { FormattedMessage } from 'react-intl';
import Tippy from '@tippyjs/react';
import { isEmpty } from 'lodash';
import { useCheckFeatureGating } from '@nile/components/src/HOC/withPlanFeatureGating';
import dayjs from 'dayjs';
import { PSA_CONSTANTS } from '@nile/components/src/Constants';

const WidgetsEventContainer = (props) => {
  const getTimeFormat = (start, end) => {
    const startDate = dayjs(start).format('YYYY-MM-DD');
    const endDate = dayjs(end).format('YYYY-MM-DD');

    if (startDate !== endDate) {
      return 'dateTime';
    } else {
      return 'time';
    }
  };

  return (
    <>
      <Card>
        <div className="card-header p10 flex fx-sb actr min-width-500 divider">
          <div>
            <TextStyle weight="SemiBold">
              <FormattedMessage id="scheduling.navigation.widget.today.events" />
            </TextStyle>
          </div>
          <div>
            <Button plain onClick={props?.onClick}>
              <FormattedMessage id="timesheet.calendarTable.viewCalendar" />
            </Button>
          </div>
        </div>

        <div className="p20 max-height-500 scroll">
          {isEmpty(props?.events) ? (
            <div className="flex actr jctr">
              <div>
                <div className="ignoreOverride">
                  <SVGIcon name="SchedulingCalendarAuthentication" />
                </div>
                <div className="mt15">
                  <TextStyle variation="subdued">
                    <FormattedMessage id="scheduling.navigation.widget.no.events.label" />
                  </TextStyle>
                </div>
              </div>
            </div>
          ) : (
            <>
              {props?.events?.map((el) => {
                const timeFormat = getTimeFormat(el?.startTime, el?.endTime);
                return (
                  <div
                    className="mb10 pointer scheduling-navigation-events"
                    onClick={props?.onClick}
                  >
                    <Card variant="secondaryCard" noShadow>
                      <div className="p10">
                        <div className="flex g10 mb10">
                          <div>
                            <SVGIcon
                              name={
                                el?.appointment?.sensitivity ===
                                PSA_CONSTANTS.SCHEDULING_APPOINTMENT_TYPE
                                  .PRIVATE
                                  ? 'Lock'
                                  : 'DatePicker'
                              }
                            />
                          </div>
                          <div>
                            <TextStyle weight="Medium">
                              {el?.appointment?.subject}
                            </TextStyle>
                          </div>
                        </div>
                        <div className="flex g3 f-12">
                          <div>
                            <TextStyle weight="Medium">
                              <DateTimeFormatter
                                type={timeFormat}
                                date={el?.startTime}
                              />
                            </TextStyle>
                          </div>
                          <div>
                            <TextStyle weight="Medium">-</TextStyle>
                          </div>
                          <div>
                            <TextStyle weight="Medium">
                              <DateTimeFormatter
                                type={timeFormat}
                                date={el?.endTime}
                              />
                            </TextStyle>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </Card>
    </>
  );
};

const SchedulingNavigationWidget = (props) => {
  // const { Section, SectionHolder, SectionIcon } = props?.styledComponents;
  const { Section, SectionHolder } = props?.styledComponents;

  const [events, setEvents] = useState([]);
  const currentUser = getCurrentUserHook();
  const schedulingPopoverRef = useRef(null);

  const {
    loading: calendarInfoLoading,
    error: calendarInfoError,
    data: calendarInfo,
    refetch: refetchIntegationState
  } = useQuery(GET_CALENDAR_INTEGRATED, {
    fetchPolicy: 'network-only',
    client: AccountClient
  });

  const calendarIntegrated =
    calendarInfo?.getLoggedInContext?.user?.calendarIntegrated;
  const date = new Date();
  const dayDetails = getDayDetails(date);

  const calendarVariables = {
    condition: {
      joinOperator: 'AND',
      operands: [
        {
          attribute: 'eventType',
          operator: 'is',
          value: 'APPOINTMENT'
        },
        {
          attribute: 'appointment.attendees.user.userId',
          operator: 'is',
          value: currentUser?.userId
        },
        {
          attribute: 'appointment.syncStatus',
          operator: 'is',
          value: 'SUCCESS'
        },
        {
          joinOperator: 'OR',
          operands: [
            {
              attribute: 'startTime',
              operator: 'between',
              value: [dayDetails?.startTime, dayDetails?.endTime]
            },
            {
              attribute: 'endTime',
              operator: 'between',
              value: [dayDetails?.startTime, dayDetails?.endTime]
            },
            {
              joinOperator: 'AND',
              operands: [
                {
                  attribute: 'startTime',
                  operator: 'before',
                  value: dayDetails?.startTime
                },
                {
                  attribute: 'endTime',
                  operator: 'after',
                  value: dayDetails?.endTime
                }
              ]
            }
          ]
        }
      ]
    }
  };

  const {
    loading,
    data: calendarEvents,
    refetch: refetchCalendarView
  } = useCachedQuery(GET_WIDGET_APPOINTMENT_COUNT, {
    client: TicketClient,
    skip: !calendarIntegrated,
    variables: {
      input: calendarVariables
    }
  });

  const sortEventsByStartTime = (events) => {
    return events.sort((a, b) => {
      const startTimeA = dayjs(a.startTime);
      const startTimeB = dayjs(b.startTime);
      return startTimeA - startTimeB;
    });
  };

  useEffect(() => {
    const allEvents = calendarEvents?.getCalendarView?.flatMap(
      (el) => el?.events
    );
    if (!isEmpty(allEvents)) {
      const sortedEvents = sortEventsByStartTime(allEvents);
      setEvents(sortedEvents);
    }
  }, [calendarEvents]);

  const resetSchedulingEvents = () => {
    setEvents([]);
    refetchIntegationState(); // Call get Current user
  };

  useEffect(() => {
    EventEmitter.subscribe(
      'refreshNavigationSchedulingWidget',
      refetchCalendarView
    );

    EventEmitter.subscribe('calendarReset', resetSchedulingEvents);

    return function cleanup() {
      EventEmitter.unsubscribe(
        'refreshNavigationSchedulingWidget',
        refetchCalendarView
      );
      EventEmitter.unsubscribe('calendarReset', resetSchedulingEvents);
    };
  }, []);

  const schedulingFeaturegating = useCheckFeatureGating('calendar_scheduling');
  const isSchedulingEnabled = schedulingFeaturegating?.enabled;
  const isSchedulingUpsell = schedulingFeaturegating?.upsell;
  const isMSP = useUserMSPCheckHook();

  if ((isSchedulingEnabled || isSchedulingUpsell) && isMSP) {
    return (
      <>
        <Section id="scheduling">
          <div
            id="scheduling-link"
            key={shortid.generate()}
            className="ptr appIcon flex flex-center full-width"
            onClick={props?.onClick}
          >
            <Tippy
              content={
                <>
                  {isSchedulingEnabled && (
                    <WidgetsEventContainer
                      events={events}
                      onClick={props?.onClick}
                    />
                  )}
                </>
              }
              placement="right"
              arrow={false}
              interactive
              theme="light"
              allowHTML
              appendTo={document.body}
              ref={(node) => {
                schedulingPopoverRef.current = node;
              }}
              offset={[0, 5]}
              maxWidth={500}
              zIndex={500}
            >
              <SectionHolder className="full-width position-relative">
                <div>
                  <SVGIcon
                    name="DatePicker"
                    color="var(--sops-app-nav-text-color)"
                  />
                </div>

                {events?.length > 0 ? (
                  <div className="app-navigation-count-bulb ">
                    {events?.length}
                  </div>
                ) : (
                  <> </>
                )}
              </SectionHolder>
            </Tippy>
          </div>
        </Section>
      </>
    );
  }

  return <></>;
};

export default SchedulingNavigationWidget;
